import {
  GamoraBase64URLGuard,
  GamoraErrorUnhandledException,
  assert,
} from '@ver.id/libs.internal.gamora';
import { HoruzProtocolChulakDataType } from '../../data.js';
import { HoruzProtocolChulakMessageGuard } from './base.js';

/**
 * Input type
 * @public
 */
export type HoruzProtocolChulakMessageAuthGuardInput = {
  compact: string;
  fresh?: boolean;
};

/**
 * HoruzProtocolChulakMessageAuthGuard
 * @public
 */
export class HoruzProtocolChulakMessageAuthGuard extends HoruzProtocolChulakMessageGuard<
  HoruzProtocolChulakDataType.AUTH,
  HoruzProtocolChulakMessageAuthGuardInput
> {
  /**
   * Valid Compact Regex
   */
  static CompactRegExp =
    /^(?<header>[a-zA-Z0-9\-_]+)\.(?<payload>[a-zA-Z0-9\-_]+)\.(?<signature>[a-zA-Z0-9\-_]+)$/;
  private _compact = '';
  private _fresh = false;

  /**
   * Constructor
   * @param input - the input
   */
  constructor(input: HoruzProtocolChulakMessageAuthGuardInput) {
    super();
    this.compact = input.compact;
    this.fresh = input?.fresh ?? false;
  }

  /**
   * Gets type
   */
  get type(): HoruzProtocolChulakDataType.AUTH {
    return HoruzProtocolChulakDataType.AUTH;
  }

  /**
   * AuthenticationCompact
   */
  get compact() {
    return this._compact;
  }
  set compact(compact: string) {
    assert(
      HoruzProtocolChulakMessageAuthGuard.CompactRegExp.test(compact),
      'Invalid compact: not a valid json web signature string.',
    );
    this._compact = compact;
  }

  /**
   * If the authentication wants a fresh start
   */
  get fresh() {
    return this._fresh;
  }
  set fresh(fresh: boolean) {
    assert(typeof fresh === 'boolean', 'Invalid fresh: not a valid boolean.');
    this._fresh = fresh;
  }

  /**
   * Gets from JSON
   * @param input - Input string
   */
  static override fromJSON(
    input: unknown,
  ): HoruzProtocolChulakMessageAuthGuard {
    assert(
      typeof input === 'object' && input !== null,
      'Invalid input argument: not an object.',
    );
    return new HoruzProtocolChulakMessageAuthGuard(
      input as HoruzProtocolChulakMessageAuthGuardInput,
    );
  }

  /**
   * Get from base64URL
   * @param input - The input
   */
  static override fromBase64URL(
    input: unknown,
  ): HoruzProtocolChulakMessageAuthGuard {
    assert(
      typeof input === 'string' && input.length > 0,
      'Invalid input argument: not a valid string.',
    );

    // Parse input
    let json;
    try {
      json = JSON.parse(GamoraBase64URLGuard.fromBase64URL(input).toUTF8());
    } catch {
      throw new GamoraErrorUnhandledException(
        'Unable to parse input: not valid JSON in base64url input.',
      );
    }

    // Create new guard
    return new HoruzProtocolChulakMessageAuthGuard(
      json as HoruzProtocolChulakMessageAuthGuardInput,
    );
  }

  /**
   * Gets JSON
   * @returns string
   */
  override toJSON() {
    return {
      compact: this.compact,
      fresh: this.fresh,
    };
  }

  /**
   * To Base64URL
   */
  override toBase64URL(): string {
    const json = this.toJSON();
    return GamoraBase64URLGuard.fromUTF8(JSON.stringify(json)).toBase64URL();
  }

  /**
   * Create clone
   */
  clone() {
    return HoruzProtocolChulakMessageAuthGuard.fromJSON(this.toJSON());
  }
}
